/**
 * Mapování QuoteData (generativní výstup) na insert do supplier_quotes.
 * Propojení DynamicQuoteCard.onSubmit → Supabase.
 *
 * Příklad použití v SupplierInquiryDetail (nebo chatové stránce pro dodavatele):
 *
 * ```tsx
 * <DynamicQuoteCard
 *   data={parsedQuote}
 *   submitting={savingQuote}
 *   onSubmit={async (data) => {
 *     setSavingQuote(true);
 *     const insert = mapQuoteToSupplierInsert(data, {
 *       supplierId: supplier.id,
 *       inquiryId: inquiry.id,
 *       region: inquiry.region,
 *     });
 *     const { error } = await supabase.from("supplier_quotes").insert(insert);
 *     if (error) {
 *       toast.error(error.message);
 *       setSavingQuote(false);
 *       return;
 *     }
 *     await fetch(..., { body: JSON.stringify({ inquiry_id: inquiry.id, total_price: data.total_price }) }); // notify-customer-quote
 *     setSavingQuote(false);
 *     toast.success("Nabídka odeslána");
 *   }}
 * />
 * ```
 */
import type { QuoteData } from "./quoteSchema";

export interface SupplierQuoteInsert {
  supplier_id: string;
  inquiry_id: string;
  total_price: number;
  region: string | null;
  notes: string | null;
  material: string | null;
  area_m2: number | null;
  material_cost: number | null;
  scaffolding_cost: number | null;
  labor_cost: number | null;
  transport_cost: number | null;
  other_costs: { label: string; value: number }[] | null;
  profit: number | null;
  vat_rate: number;
}

/** Výchozí DPH pro nabídky */
const DEFAULT_VAT_RATE = 12;

/**
 * Převod QuoteData na formát pro supplier_quotes.insert.
 * Mapuje známé parametry (material, area_m2, scaffolding_included) a zbytek dává do notes.
 */
export function mapQuoteToSupplierInsert(
  quote: QuoteData,
  context: { supplierId: string; inquiryId: string; region?: string | null }
): SupplierQuoteInsert {
  const parts: string[] = [];

  if (quote.start_date || quote.end_date) {
    parts.push(`Termín: ${quote.start_date || "?"} – ${quote.end_date || "?"}`);
  }

  const knownParams = new Map<string, string | number>();
  const otherCosts: { label: string; value: number }[] = [];

  for (const p of quote.dynamic_parameters) {
    const v = p.value;
    if (v === null || v === "") continue;

    switch (p.id) {
      case "material":
        knownParams.set("material", String(v));
        break;
      case "area_m2":
      case "area":
        knownParams.set("area_m2", typeof v === "number" ? v : parseFloat(String(v)) || 0);
        break;
      case "scaffolding_included":
        if (v === true) parts.push("Lešení v ceně");
        break;
      case "material_cost":
      case "labor_cost":
      case "transport_cost":
        if (typeof v === "number" && v > 0) {
          otherCosts.push({ label: p.label, value: v });
        }
        break;
      default:
        parts.push(`${p.label}: ${v}`);
    }
  }

  const notes = parts.length > 0 ? parts.join("\n") : null;

  return {
    supplier_id: context.supplierId,
    inquiry_id: context.inquiryId,
    total_price: quote.total_price!,
    region: context.region ?? null,
    notes,
    material: (knownParams.get("material") as string) ?? null,
    area_m2: (knownParams.get("area_m2") as number) ?? null,
    material_cost: null,
    scaffolding_cost: null,
    labor_cost: null,
    transport_cost: null,
    other_costs: otherCosts.length > 0 ? otherCosts : null,
    profit: null,
    vat_rate: DEFAULT_VAT_RATE,
  };
}
